import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  CalendarDays, CalendarOff, KeyRound, Laptop, Loader2, Play, Square, PartyPopper, Wallet,
} from 'lucide-react';
import { Button } from '../../../components/Button';
import { useCheckInOut } from '../../../components/attendance/CheckInOut';
import { useRbacStore } from '../../../store/rbacStore';
import { useEmployeeAssetsStore, type EmployeeHoldings } from '../../../store/employeeAssetsStore';
import type { MyDashboard, PeopleDashboardData } from '../../../store/peopleDashboardStore';
import { CHART, CHART_AXIS, CHART_GRID, CHART_TOOLTIP } from '../../../lib/chartColors';
import { cn } from '../../../lib/utils';
import {
  DayStatusBadge, Empty, Panel, RequestStatusBadge, daysFrom, fmtClock, fmtDay, fmtHours, fmtMoney,
  fmtRange, fmtTime, fmtWeekday, parseDay,
} from './widgets';

/** Standard day used for the target line on the weekly chart. */
const WORKDAY_HOURS = 8;

/* ─────────────────────────── attendance card ─────────────────────────── */

function AttendanceCard({ me, onChanged }: {
  me: MyDashboard; onChanged: () => void;
}) {
  // Same control as the header and the Attendance page: flips instantly, and
  // every copy on screen updates together (shared attendance store).
  const { today, working: liveWorking, available, requestToggle, confirmDialog } = useCheckInOut(onChanged);
  const [now, setNow] = useState(Date.now());
  const a = me.attendance;
  const working = today ? liveWorking : a.status === 'checked_in';
  const openSince = today ? today.activeSession?.checkIn ?? null : a.openSince;

  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [working]);
  const closedHours = today
    ? today.sessions.reduce((n, s) => n + (s.status !== 'OPEN' && s.status !== 'SUPERSEDED' ? s.creditedMinutes || 0 : 0), 0) / 60
    : a.hours;
  const liveHours = closedHours + (working && openSince ? Math.max(0, now - new Date(openSince).getTime()) / 3_600_000 : 0);
  const status: MyDashboard['attendance']['status'] = a.status === 'on_leave' ? 'on_leave'
    : !today ? a.status
    : working ? 'checked_in' : today.state === 'CHECKED_OUT' ? 'checked_out' : 'not_checked_in';
  const lastOut = today
    ? today.sessions.map((s) => s.checkOut).filter(Boolean).sort().pop() ?? null
    : a.lastCheckout;
  const sessions = today ? today.sessions.filter((s) => s.status !== 'SUPERSEDED').length : a.sessions + (working ? 1 : 0);
  const subline =
    status === 'checked_in' ? `Since ${fmtTime(openSince)}${sessions > 1 ? ` · ${sessions} sessions` : ''}`
    : status === 'checked_out' ? `Last out ${fmtTime(lastOut)} · ${sessions} ${sessions === 1 ? 'session' : 'sessions'}`
    : status === 'on_leave' ? 'You are on approved leave today'
    : 'You have not checked in yet';

  // Checking in is the one action every employee takes daily, so the card is
  // the dashboard's anchor: a primary-accented surface with a prominent CTA.
  // Check-out stays a quieter secondary so an open session isn't closed by a
  // stray tap. On leave there is nothing to do, so the accent is dropped.
  const canAct = status !== 'on_leave' && (today ? available || working : true);
  const accent = status !== 'on_leave';

  return (
    <Panel
      title="Today"
      link={{ label: 'Attendance', to: '/people/attendance' }}
      className={cn(accent && 'ring-1 ring-primary/25 bg-primary/[0.03]')}
    >
      <div className="flex items-center justify-between gap-2">
        <DayStatusBadge status={status} />
        <span className="text-xs tabular-nums text-muted-foreground">{fmtTime(new Date(now).toISOString())}</span>
      </div>
      <div className="mt-3 font-display text-metric tabular-nums leading-none text-foreground">{fmtHours(liveHours)}</div>
      <p className="mt-1.5 text-xs text-muted-foreground">{subline}</p>
      {canAct && (
        <Button
          variant={working ? 'secondary' : 'primary'}
          className="mt-4 w-full"
          onClick={requestToggle}
        >
          {working ? <Square /> : <Play />}
          {working ? 'Check out' : 'Check in'}
        </Button>
      )}
      {confirmDialog}
    </Panel>
  );
}

/* ──────────────────────────── leave balance ──────────────────────────── */

/** Everyday leave first; parental/bereavement leave rarely matters at a glance. */
const LEAVE_PRIORITY = ['CL', 'SL', 'AL', 'EL', 'PL', 'ML', 'BL'];

function LeaveCard({ me }: { me: MyDashboard }) {
  const balances = [...me.leaveBalances]
    .filter((b) => b.allocated > 0)
    .sort((x, y) => {
      const ix = LEAVE_PRIORITY.indexOf(x.code); const iy = LEAVE_PRIORITY.indexOf(y.code);
      return (ix < 0 ? 99 : ix) - (iy < 0 ? 99 : iy);
    })
    .slice(0, 3);

  return (
    <Panel title="Leave balance" link={{ label: 'Apply', to: '/people/leave' }}>
      {balances.length === 0 ? (
        <Empty icon={<CalendarOff />} text="No leave allocated for this year" />
      ) : (
        <ul className="space-y-3">
          {balances.map((b) => (
            <li key={b.code}>
              <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                <span className="truncate text-foreground">{b.name}</span>
                <span className="tabular-nums text-muted-foreground">
                  <span className="font-semibold text-foreground">{b.remaining}</span> / {b.allocated}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                <div
                  className={cn('h-full rounded-full', b.remaining / b.allocated < 0.2 ? 'bg-warning' : 'bg-primary')}
                  style={{ width: `${Math.min(100, Math.round((b.remaining / b.allocated) * 100))}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ────────────────────────────── upcoming ─────────────────────────────── */

function UpcomingCard({ me, today }: { me: MyDashboard; today: string }) {
  const items = me.leaveRequests.slice(0, 3);
  return (
    <Panel title="My requests" link={{ label: 'View', to: '/people/leave' }}>
      <div className="space-y-3">
        {items.length === 0 && !me.nextShift && me.pendingRegularizations === 0 && (
          <Empty icon={<CalendarDays />} text="No upcoming leave or shifts" className="min-h-0 py-3" />
        )}
        {items.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground">{r.type}</p>
              <p className="text-xs text-muted-foreground">{fmtRange(r.startDate, r.endDate)} · {r.days}d</p>
            </div>
            <RequestStatusBadge status={r.status} />
          </div>
        ))}
        {me.nextShift && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground">Shift · <span className="capitalize">{me.nextShift.name}</span></p>
              <p className="text-xs text-muted-foreground">
                {fmtWeekday(me.nextShift.day)}
                {me.nextShift.start ? ` · ${fmtClock(me.nextShift.start)}–${fmtClock(me.nextShift.end)}` : ''}
              </p>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{daysFrom(today, me.nextShift.day)}</span>
          </div>
        )}
        {me.pendingRegularizations > 0 && (
          <Link to="/people/attendance" className="flex items-center justify-between gap-2 border-t border-border pt-3 text-sm">
            <span className="text-foreground">Attendance corrections</span>
            <RequestStatusBadge status="pending" />
          </Link>
        )}
      </div>
    </Panel>
  );
}

/* ─────────────────────────────── payslip ─────────────────────────────── */

function PayslipCard({ me }: { me: MyDashboard }) {
  const p = me.latestPayslip;
  return (
    <Panel title="Latest payslip" link={{ label: 'Payroll', to: '/people/payroll' }}>
      {!p ? (
        <Empty icon={<Wallet />} text="No payslip has been issued yet" />
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {parseDay(p.periodEnd).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
            <RequestStatusBadge status={p.status} />
          </div>
          <div className="mt-3 font-display text-metric tabular-nums leading-none text-foreground">{fmtMoney(p.net, p.currency)}</div>
          <p className="mt-1.5 text-xs text-muted-foreground">Net pay · gross {fmtMoney(p.gross, p.currency)}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            {p.paymentDate ? `Paid on ${fmtDay(p.paymentDate)}` : `Period ${fmtRange(p.periodStart, p.periodEnd)}`}
          </p>
        </>
      )}
    </Panel>
  );
}

/* ────────────────────────────── exported ─────────────────────────────── */

/** Row of four personal cards: today, leave, requests, payslip. */
export function MyDayCards({ data, onChanged }: {
  data: PeopleDashboardData; onChanged: () => void;
}) {
  const me = data.me!;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <AttendanceCard me={me} onChanged={onChanged} />
      <LeaveCard me={me} />
      <UpcomingCard me={me} today={data.today} />
      <PayslipCard me={me} />
    </div>
  );
}

export function MyWeekPanel({ me, today, className }: { me: MyDashboard; today: string; className?: string }) {
  const rows = me.week.map((d) => ({
    ...d,
    label: parseDay(d.date).toLocaleDateString(undefined, { weekday: 'short' }),
    isToday: d.date === today,
  }));
  const total = rows.reduce((s, r) => s + r.hours, 0);
  const worked = rows.filter((r) => r.hours > 0).length;

  return (
    <Panel
      title="My last 7 days"
      description={`${fmtHours(total)} worked across ${worked} ${worked === 1 ? 'day' : 'days'}`}
      link={{ label: 'History', to: '/people/attendance' }}
      className={className}
    >
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART_GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: CHART_AXIS }} axisLine={false} tickLine={false} />
            <ReferenceLine y={WORKDAY_HOURS} stroke={CHART_AXIS} strokeDasharray="4 4" />
            <Tooltip
              contentStyle={CHART_TOOLTIP}
              cursor={{ fill: 'hsl(var(--secondary))' }}
              formatter={(v: any) => [fmtHours(Number(v)), 'Worked']}
              labelFormatter={(_l: any, p: any) => (p?.[0]?.payload ? fmtWeekday(p[0].payload.date) : '')}
            />
            <Bar dataKey="hours" radius={[4, 4, 0, 0]} fill={CHART.blue} maxBarSize={36} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

export function HolidaysPanel({ data, className }: { data: PeopleDashboardData; className?: string }) {
  // Everyone sees the next holidays here; only roles with the Holidays screen get the link.
  const canOpen = useRbacStore((s) => s.hasPermission('People', 'Holidays', 'View'));
  return (
    <Panel
      title="Upcoming holidays"
      link={canOpen ? { label: 'Calendar', to: '/people/holidays' } : undefined}
      className={className}
    >
      {data.holidays.length === 0 ? (
        <Empty icon={<PartyPopper />} text="No upcoming holidays published" />
      ) : (
        <ul className="divide-y divide-border">
          {data.holidays.map((h) => {
            const d = parseDay(h.date);
            return (
              <li key={`${h.date}-${h.name}`} className="flex items-center gap-3 py-2">
                <div className="flex size-11 shrink-0 flex-col items-center justify-center rounded-md border border-border bg-secondary leading-none">
                  <span className="text-xs font-medium text-muted-foreground">
                    {d.toLocaleDateString(undefined, { month: 'short' })}
                  </span>
                  <span className="text-sm font-semibold text-foreground">{d.getDate()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground">{h.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {d.toLocaleDateString(undefined, { weekday: 'long' })}{h.type ? ` · ${h.type}` : ''}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{daysFrom(data.today, h.date)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}


/** Devices and licence seats the signed-in employee holds (Asset Hub, /assets/employees/me). */
export function MyAssetsPanel({ className }: { className?: string }) {
  const { fetchMyHoldings } = useEmployeeAssetsStore();
  const [data, setData] = useState<EmployeeHoldings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyHoldings().then(setData).catch((e) => setError(e.message));
  }, [fetchMyHoldings]);

  return (
    <Panel title="My assets" description="Devices and software licences assigned to you" link={{ label: 'Request an asset', to: '/assets/requests' }} className={className}>
      {error ? (
        <Empty icon={<Laptop />} text={error} />
      ) : !data ? (
        <Empty icon={<Loader2 className="animate-spin" />} text="Loading…" />
      ) : data.assets.length === 0 && data.licenses.length === 0 ? (
        <Empty icon={<Laptop />} text="No assets or licences assigned to you" />
      ) : (
        <ul className="divide-y divide-border">
          {data.assets.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-2">
              <Laptop className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{a.name} <span className="text-xs text-muted-foreground">{a.assetCode}</span></p>
                <p className="truncate text-xs text-muted-foreground">{[a.category, a.serialNumber && `S/N ${a.serialNumber}`].filter(Boolean).join(' · ') || '—'}</p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">{a.assignedDate ? `Since ${fmtDay(a.assignedDate)}` : ''}</span>
            </li>
          ))}
          {data.licenses.map((l) => (
            <li key={l.assignmentId} className="flex items-center gap-3 py-2">
              <KeyRound className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{l.softwareName}</p>
                <p className="truncate text-xs text-muted-foreground">{l.publisher || 'Software licence'}</p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">{l.expiryDate ? `Until ${fmtDay(l.expiryDate)}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
